import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useConfig } from "../config";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const YEAR = new Date().getFullYear();

const STEPS = [
  {
    title: "We learn your product",
    body: "Share a demo login and a short brief. TryLook maps your app so the guide knows where every feature lives and what it's for.",
  },
  {
    title: "The guide runs the demo",
    body: "Buyers talk naturally. The guide asks discovery questions, shows the parts they care about, and sticks to what your material says.",
  },
  {
    title: "Qualified calls land on your calendar",
    body: "When there's a fit, the guide agrees a time, sends the invite, and hands your team a summary of what the buyer needs.",
  },
];

const FEATURES = [
  {
    title: "Discovery that qualifies",
    body: "Company, role, team size, use case and timeline are captured as the conversation happens, not in a form nobody finishes.",
  },
  {
    title: "Answers you can stand behind",
    body: "Grounded in your own brief. When the guide doesn't know, it promises a follow-up instead of inventing features or pricing.",
  },
  {
    title: "Booked, not just interested",
    body: "It confirms the time and timezone out loud, then emails a calendar invite to the buyer and your team.",
  },
  {
    title: "Personal demo links",
    body: "Send a lead their own expiring link straight from the dashboard. Resend or revoke it any time.",
  },
  {
    title: "One line on your website",
    body: "Add the embed tag and a “Book a live demo” button opens the call right on your page.",
  },
  {
    title: "Clear AI disclosure",
    body: "Prospects know they're talking with an AI guide before the call starts, and demos run against your sandbox account, not customer data.",
  },
];

function CallIllustration() {
  return (
    <div className="call-mock" aria-hidden="true" data-tour="sample-call">
      <div className="call-mock-bar">
        <span>demo.yourproduct.com</span>
        <span className="call-mock-live">Live with TryLook guide</span>
      </div>
      <div className="call-mock-screen">
        <div className="mock-sidebar">
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="mock-panel">
          <span className="mock-line" style={{ width: "46%" }} />
          <div className="mock-bars">
            <span style={{ height: "38%" }} />
            <span style={{ height: "56%" }} />
            <span style={{ height: "44%" }} />
            <span style={{ height: "82%" }} />
            <span style={{ height: "64%" }} />
          </div>
        </div>
      </div>
      <div className="call-mock-transcript">
        <p className="bubble bubble-prospect">Can it qualify leads before they reach my team?</p>
        <p className="bubble bubble-guide">
          Yes. I ask about team size, timeline and use case, and only book a call when it's a fit. Here's where
          that shows up in your pipeline.
        </p>
      </div>
      <p className="call-mock-booked">✓ Call booked · Thursday, 3:30 PM IST</p>
    </div>
  );
}

export function LandingPage() {
  useDocumentTitle("TryLook · Live AI product demos");
  const { siteUrl } = useConfig();
  const snippet = `<script src="${siteUrl}/embed.js"\n        data-trylook-key="ek_your_key" async></script>`;

  return (
    <div className="landing">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="container header-row">
          <Logo />
          <nav className="site-nav" aria-label="Primary">
            <a href="#how-it-works">How it works</a>
            <a href="#features">Features</a>
            <a href="#embed">Embed</a>
          </nav>
          <div className="header-actions">
            <Link to="/app" className="btn btn-ghost btn-sm">
              Sign in
            </Link>
            <Link to="/demo" className="btn btn-accent btn-sm">
              Book a demo
            </Link>
          </div>
        </div>
      </header>

      <main id="main">
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <p className="eyebrow">Live AI product demos</p>
              <h1 className="hero-title">Every buyer gets a live demo. The moment they ask.</h1>
              <p className="hero-lede">
                TryLook's AI guide runs discovery, walks prospects through your real product, answers from your own
                material, and books the call with your team. Around the clock, from your website or a link in their
                inbox.
              </p>
              <div className="hero-actions">
                <Link to="/demo" className="btn btn-accent btn-lg">
                  Book a demo
                </Link>
                <a href="#how-it-works" className="btn btn-secondary btn-lg">
                  See how it works
                </a>
              </div>
              <p className="hero-note">
                That button starts a real call with our own guide. It's the quickest way to see exactly what your
                buyers would get.
              </p>
            </div>
            <CallIllustration />
          </div>
        </section>

        <section id="how-it-works" className="section section-alt">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">How it works</p>
              <h2>From brief to booked call, without the back-and-forth</h2>
              <p>Your team stops repeating the same first demo. Buyers stop waiting days to see the product.</p>
            </div>
            <ol className="steps" data-tour="steps">
              {STEPS.map((step) => (
                <li key={step.title} className="step">
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="features" className="section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Built for revenue teams</p>
              <h2>A first call that qualifies, informs, and converts</h2>
            </div>
            <div className="features" data-tour="feature-cards">
              {FEATURES.map((feature) => (
                <article key={feature.title} className="feature">
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="embed" className="section section-alt">
          <div className="container embed-band">
            <div>
              <p className="eyebrow">Website embed</p>
              <h2>Add live demos to your site in one line</h2>
              <p className="muted">
                Once your workspace is ready, copy your tag from the dashboard and paste it before the closing body
                tag. It works with any site builder, and the call opens right on your page.
              </p>
            </div>
            <pre className="code-block">
              <code>{snippet}</code>
            </pre>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="cta-band">
              <h2>See it on us</h2>
              <p>Our own AI guide will run your discovery call, show you TryLook, and find a time with the team.</p>
              <Link to="/demo" className="btn btn-accent btn-lg">
                Book a demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-row">
          <span>© {YEAR} TryLook</span>
          <nav className="footer-links" aria-label="Footer">
            <Link to="/demo">Book a demo</Link>
            <Link to="/app">Sign in</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
